import { type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { MetricPoint } from '@pf/contracts';
import { formatTime } from '@/lib/datetime';
import { cn } from '@/lib/utils';

/**
 * Chart wrappers for the dashboard.
 *
 * Every chart in this app follows the same five rules, which is why they are wrapped rather than
 * used directly:
 *
 * 1. **Colour comes from theme tokens**, not from literals, so light and dark are both deliberate
 *    and a series keeps its identity across them.
 * 2. **Axes stay quiet.** Thin ticks, no chart junk, gridlines only where they help read a value.
 * 3. **Every chart has a text equivalent.** A `<figcaption>`-style summary and a visually hidden
 *    description mean the data is available to a screen reader, which an SVG of `<path>` elements
 *    is not.
 * 4. **Tooltips are styled once.** A default Recharts tooltip is a white box that is unreadable in
 *    dark mode.
 * 5. **Every word comes from the caller, already translated.** Nothing in this directory calls
 *    `useTranslation`. Series names, column headers and descriptions all arrive as finished
 *    strings, the way `description` always has. That is not squeamishness about hooks: it keeps a
 *    chart renderable from a test, a story or a future PDF/email path where no i18n provider is
 *    mounted, and it keeps the locale keys beside the page that owns the feature rather than
 *    scattered through the presentation layer. It also forces the *accuracy* — one
 *    {@link StateBarChart} draws deposit transactions on one card and withdrawal requests on the
 *    next, and only the caller knows which of the two it is looking at. A component that named its
 *    own series would be wrong on one of them whatever noun it picked.
 * 6. **`sr-only` goes on a wrapping `div`, never on the `<table>` itself.** This one is a bug fix
 *    written down as a rule, because the broken spelling is the obvious one and it fails silently
 *    on desktop.
 *
 *    `sr-only` is absolute positioning plus `width:1px`, `height:1px`, `overflow:hidden`,
 *    `clip:rect(0,0,0,0)` and `white-space:nowrap`. On a block box that is exactly 1×1 and
 *    clipped. On a `<table>` it is
 *    not: automatic table layout resolves a table's used width as `max(specified, min-content)`,
 *    so `width:1px` is only a *suggestion* the table is free to ignore — and `white-space:nowrap`,
 *    which `sr-only` sets so screen readers do not run cells together, forces min-content up to
 *    the full unwrapped width of the longest row. The table's own border box therefore ends up
 *    hundreds of pixels wide. `overflow:hidden` clips what is *inside* it and does nothing about
 *    that; being absolutely positioned, the box still contributes to the scrollable overflow of
 *    the initial containing block.
 *
 *    Nothing is visible, so nothing looks wrong — until a phone. A mobile browser sees a document
 *    wider than the viewport and zooms out to fit it, which is why the symptom is not "a stray
 *    scrollbar" but "the whole dashboard is small and does not fill the screen". Measured at a
 *    390px viewport on 2026-08-07: the scatter's table was 590px and the histogram's 537px, giving
 *    `documentElement.scrollWidth` 626 against a 390 viewport.
 *
 *    Wrapping in a `div.sr-only` fixes it because a block box does honour `width:1px`, and
 *    overflow clipped by an ancestor does not propagate outward. The table keeps its semantics
 *    untouched — the wrapper is presentational, and clipped content is still in the accessibility
 *    tree, which is the entire premise of the `sr-only` pattern.
 */

/** Shared tooltip surface. */
function ChartTooltip({
  active,
  payload,
  label,
  valueFormatter,
}: {
  active?: boolean | undefined;
  payload?:
    | readonly {
        value?: number | undefined;
        name?: string | undefined;
        color?: string | undefined;
      }[]
    | undefined;
  label?: string | number | undefined;
  valueFormatter: (value: number) => string;
}): ReactNode {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      {label !== undefined ? (
        <p className="pb-1 font-medium text-popover-foreground">{String(label)}</p>
      ) : null}
      <ul className="space-y-0.5">
        {payload.map((entry, index) => (
          <li key={index} className="flex items-center gap-2 text-muted-foreground">
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: entry.color }}
              aria-hidden="true"
            />
            <span>{entry.name}</span>
            <span className="tnum ml-auto font-medium text-popover-foreground">
              {valueFormatter(entry.value ?? 0)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Props for {@link TrendChart}. */
export interface TrendChartProps {
  /** Series points. */
  points: readonly MetricPoint[];
  /** Accessible description of what the chart shows. */
  description: string;
  /**
   * The series' name, already translated — e.g. "Reservations / second".
   *
   * There is exactly one series here, so this string is the entire legend: it is the only thing
   * naming what the number in the tooltip *is*. Required rather than defaulted, because a default
   * would be a generic noun ("Value") that is true of every chart and informative about none, and
   * because an optional English fallback is how the hardcoded string got here in the first place.
   * The KPI the tile is already showing is normally the right answer — pass its label.
   */
  seriesName: string;
  /** Formats a value for the tooltip and the axis. */
  valueFormatter?: ((value: number) => string) | undefined;
  /** Which theme chart colour to use, 1–5. */
  colorIndex?: 1 | 2 | 3 | 4 | 5 | undefined;
  /** Chart height in pixels. */
  height?: number | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

/** A filled area trend, used for the headline KPIs. */
export function TrendChart({
  points,
  description,
  seriesName,
  valueFormatter = (v) => v.toFixed(1),
  colorIndex = 1,
  height = 140,
  className,
}: TrendChartProps): ReactNode {
  const data = points.map((p) => ({ at: formatTime(p.at).slice(0, 5), value: p.value }));
  const color = `hsl(var(--chart-${String(colorIndex)}))`;
  const gradientId = `trend-${String(colorIndex)}`;

  return (
    <figure className={cn('w-full', className)}>
      <figcaption className="sr-only">{description}</figcaption>
      <div style={{ height }} role="img" aria-label={description}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="at"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={38}
              tickFormatter={valueFormatter}
            />
            <RechartsTooltip
              content={<ChartTooltip valueFormatter={valueFormatter} />}
              cursor={{ stroke: 'hsl(var(--border))' }}
            />
            <Area
              type="monotone"
              dataKey="value"
              name={seriesName}
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </figure>
  );
}

/** Props for {@link MultiLineChart}. */
export interface MultiLineChartProps {
  /** Named series, all sharing the same x values. */
  series: readonly {
    name: string;
    points: readonly MetricPoint[];
    colorIndex: 1 | 2 | 3 | 4 | 5;
  }[];
  /** Accessible description. */
  description: string;
  /** Formats a value. */
  valueFormatter?: ((value: number) => string) | undefined;
  /** Chart height. */
  height?: number | undefined;
}

/** Several lines on one axis — used for provider latency percentiles. */
export function MultiLineChart({
  series,
  description,
  valueFormatter = (v) => String(Math.round(v)),
  height = 200,
}: MultiLineChartProps): ReactNode {
  const first = series[0];
  if (!first) return null;
  const data = first.points.map((point, index) => {
    const row: Record<string, number | string> = { at: formatTime(point.at).slice(0, 5) };
    for (const s of series) row[s.name] = s.points[index]?.value ?? 0;
    return row;
  });

  return (
    <figure className="w-full">
      <figcaption className="sr-only">{description}</figcaption>
      <div style={{ height }} role="img" aria-label={description}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="at"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={44}
              tickFormatter={valueFormatter}
            />
            <RechartsTooltip
              content={<ChartTooltip valueFormatter={valueFormatter} />}
              cursor={{ stroke: 'hsl(var(--border))' }}
            />
            {series.map((s) => (
              <Line
                key={s.name}
                type="monotone"
                dataKey={s.name}
                name={s.name}
                stroke={`hsl(var(--chart-${String(s.colorIndex)}))`}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <ul className="flex flex-wrap gap-4 pt-2 text-2xs text-muted-foreground">
        {series.map((s) => (
          <li key={s.name} className="flex items-center gap-1.5">
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: `hsl(var(--chart-${String(s.colorIndex)}))` }}
              aria-hidden="true"
            />
            {s.name}
          </li>
        ))}
      </ul>
    </figure>
  );
}

/**
 * Every piece of text {@link StateBarChart} renders, already translated (rule 5).
 *
 * `series` is the load-bearing field, and it is a correctness fix before it is an i18n one. The
 * dashboard draws this chart twice — once over deposit transactions, once over withdrawal
 * requests — so any noun the component chose for itself would be a lie on one of the two cards.
 * Naming the series at the call site is what lets the withdrawal tooltip say "withdrawal
 * requests"; translating it is what lets it say so in Thai.
 *
 * `state`/`count` head the visually hidden table, and they matter more than their size suggests:
 * under jsdom, under a screen reader, and in any context that cannot read an SVG of `<path>`
 * elements, that table *is* the chart. Leaving its two headers in English would mean the only
 * version of this chart a Thai screen-reader user ever gets is the untranslated one.
 */
export interface StateBarChartLabels {
  /** The bar series' name — the tooltip's measure row, e.g. "Deposit transactions". */
  series: string;
  /** Category column header for the text equivalent, e.g. "State". */
  state: string;
  /** Value column header for the text equivalent, e.g. "Count". */
  count: string;
}

/** Props for {@link StateBarChart}. */
export interface StateBarChartProps {
  /** Bars, each with its own colour. */
  data: readonly { label: string; value: number; color: string }[];
  /** Accessible description. */
  description: string;
  /** Translated chart text. Required, so a new call site cannot silently ship English. */
  labels: StateBarChartLabels;
  /** Chart height. */
  height?: number | undefined;
}

/** A horizontal census of states — the deposit and withdrawal funnels. */
export function StateBarChart({
  data,
  description,
  labels,
  height = 220,
}: StateBarChartProps): ReactNode {
  return (
    <figure className="w-full">
      <figcaption className="sr-only">{description}</figcaption>
      <div style={{ height }} role="img" aria-label={description}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={[...data]}
            layout="vertical"
            margin={{ top: 0, right: 16, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={118}
            />
            <RechartsTooltip
              content={<ChartTooltip valueFormatter={(v) => String(Math.round(v))} />}
              cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
            />
            <Bar
              dataKey="value"
              name={labels.series}
              radius={[0, 4, 4, 0]}
              isAnimationActive={false}
            >
              {data.map((entry) => (
                <Cell key={entry.label} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Text equivalent — the chart is decorative once this table exists. Wrapper, not the
          table, carries `sr-only` — rule 6. */}
      <div className="sr-only">
        <table>
          <caption>{description}</caption>
          <thead>
            <tr>
              <th scope="col">{labels.state}</th>
              <th scope="col">{labels.count}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((entry) => (
              <tr key={entry.label}>
                <th scope="row">{entry.label}</th>
                <td>{entry.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
